/**
 * Phase 14 — master mutations: create one, make one out of a container,
 * delete (optionally detaching followers), a container's follow write, and
 * a push of the master's sections onto its followers (21/10/2026).
 * One invalidate over the Drop Generator root key, like the other hooks.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DropGenMasterPushResult, DropGenOwnSections, DropGenSection } from '@trafficpulse/shared';

import { api } from '@/services/api';

import { dropGeneratorKeys } from '../useDropGenerator';

type Json = Record<string, unknown>;
interface Envelope<T> {
  data: T;
}
const BASE = '/drop-generator';

export function useMasterMutations() {
  const queryClient = useQueryClient();
  // In the background — see useDropGeneratorMutations (an awaited refresh held every Save step).
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: dropGeneratorKeys.all });
  };
  const createMaster = useMutation({
    mutationFn: async (body: Json) => (await api.post<Envelope<Json>>(`${BASE}/masters`, body)).data.data,
    onSuccess: invalidate,
  });
  const createMasterFromContainer = useMutation({
    mutationFn: async (input: { containerId: string; name: string; color?: string }) =>
      (await api.post<Envelope<Json>>(`${BASE}/masters/from-container/${input.containerId}`, {
        name: input.name,
        ...(input.color ? { color: input.color } : {}),
      })).data.data,
    onSuccess: invalidate,
  });
  const removeMaster = useMutation({
    mutationFn: async (input: { id: string }) =>
      (await api.delete<Envelope<Json>>(`${BASE}/masters/${input.id}`)).data.data,
    onSuccess: invalidate,
  });
  const followMaster = useMutation({
    mutationFn: async (input: { id: string; masterId: string | null; ownSections?: DropGenOwnSections; campaignWiserId?: number }) =>
      (await api.patch<Envelope<Json>>(`${BASE}/containers/${input.id}/master`, {
        masterId: input.masterId,
        ownSections: input.ownSections ?? {},
        ...(input.campaignWiserId !== undefined ? { campaignWiserId: input.campaignWiserId } : {}),
      })).data.data,
    onSuccess: invalidate,
  });
  const pushMaster = useMutation({
    mutationFn: async (input: { id: string; sections?: DropGenSection[] }) =>
      (await api.post<Envelope<DropGenMasterPushResult>>(`${BASE}/masters/${input.id}/push`, input.sections ? { sections: input.sections } : {})).data.data,
    onSuccess: invalidate,
  });
  return { createMaster, createMasterFromContainer, removeMaster, followMaster, pushMaster };
}
